import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '/Foundation/SitecoreGraphQL/code/clientv2.js';
import { getAdditionalFilterForOptions } from '../../FitAssistantShared/Functions.js';
import { getWindowData } from '../../FitAssistantShared/GetWindowData.js';
import { getPerCategorySearchString, getSearchString } from '../Util/SearchString.ts';
import { GET_OPTIONS_QUERY } from '../Queries/Queries.ts';
import { RootState } from '../Store/store.ts';
import { CarInformation, FitAssistantState, Step, StepData } from '../Types/FitAssistantData.ts';
import { GetOptionsQuery, QueryVariables } from '../Types/FitAssistantGraphQLTypes.ts';
import { FetchListOptionArgs, RejectValue } from './Types.ts';
import { addCarInformation, setStepEnum } from '../Slices/FitAssistantSlice.ts';
import FitAssistantStep from '../Enums/FitAssistantStep.ts';
import FitAssistantVariation from '../Enums/FitAssistantVariation.ts';
import {
  getTentWeightForMatchingProducts,
  rackOptionSupportsTentWeight,
} from '../Helpers/RooftopTentWeightHelper.ts';

export const fetchListOptions = createAsyncThunk<StepData, FetchListOptionArgs, RejectValue>(
  'fitAssistant/fetchListOptions',
  async ({ text, value, parameter }, { getState, dispatch, rejectWithValue }) => {
    let state = getState() as RootState;

    let updatedCarInformation: CarInformation[] = updateCarInfoList(text, value, parameter, state.FitAssistant);
    dispatch(addCarInformation(updatedCarInformation));

    const searchString: string = getSearchString(updatedCarInformation);
    const perCategorySelection = getPerCategorySearchString(updatedCarInformation);

    let [configurationItem, nextStep] = GetConfigurationData(updatedCarInformation, state.FitAssistant);

    const getOptionsVariables: QueryVariables = {
      siteName: getWindowData().SitecoreSiteName,
      language: getWindowData().SitecoreLanguage,
      configurationItem: configurationItem,
      parameters: `${searchString},${perCategorySelection}`,
      additionalFilter: getAdditionalFilterForOptions(nextStep, state.FitAssistant.initialData.AdditionalFilters),
      ispreview: state.FitAssistant.initialData.IsPreview,
    };

    let result = await client.query<GetOptionsQuery>(GET_OPTIONS_QUERY, getOptionsVariables);

    if (result.error) return rejectWithValue(result.error);

    const stateAfterRequest = getState() as RootState;

    let stepToAdd: StepData = {
      data: result.data.getCarSelectorOptions.carSelectorOptionsResponse.Options,
      messages: result.data.getCarSelectorOptions.carSelectorOptionsResponse.OptionMessages,
      parameters: result.data.getCarSelectorOptions.carSelectorOptionsResponse.ParameterName,
      configItems: result.data.getCarSelectorOptions.carSelectorOptionsResponse.NextConfigurationItem,
    };

    if (
      stepToAdd.parameters === FitAssistantStep.RoofRacks &&
      stateAfterRequest.FitAssistant.initialData.FitAssistantVariation === FitAssistantVariation.RooftopTents
    ) {
      const tentWeight = getTentWeightForMatchingProducts(
        stateAfterRequest.MatchingProducts.matchingProducts ?? undefined,
        stateAfterRequest.ProductDetail?.ProductId
      );
      stepToAdd = {
        ...stepToAdd,
        data: stepToAdd.data.filter((opt) => rackOptionSupportsTentWeight(opt, tentWeight)),
      };
    }

    if (stepToAdd.parameters === FitAssistantStep.RoofRacks) {
      if (stepToAdd.data.length > 0) {
        dispatch(setStepEnum(FitAssistantStep.Confirmed));
      } else {
        dispatch(setStepEnum(FitAssistantStep.ConfirmedNoRacks));
      }
    }

    if (stepToAdd.data.length === 0 && stepToAdd.configItems === null) {
      const lastCarInfoWithoutData: CarInformation[] = updateCarInfoList(
        'no-value',
        '0',
        stepToAdd.parameters,
        state.FitAssistant
      );

      dispatch(addCarInformation(lastCarInfoWithoutData));
    }

    return stepToAdd;
  }
);

const updateCarInfoList = (
  text: string,
  value: string,
  parameter: string,
  state: FitAssistantState
): CarInformation[] => {
  const getDuplicatesIndex = state.carInformation.findIndex((duplicate) => duplicate.parameterName === parameter);

  let newCarInformation: CarInformation[];

  if (getDuplicatesIndex >= 0) {
    newCarInformation = state.carInformation.map((item, i) =>
      i === getDuplicatesIndex
        ? {
            ...item,
            text,
            value,
            configurationItem:
              state.stepData[state.stepData.length - 1].configItems === null
                ? state.stepData[state.stepData.length - 2].configItems
                : item.configurationItem,
          }
        : item
    );
  } else {
    newCarInformation = state.carInformation.concat({
      parameterName: parameter,
      configurationItem:
        state.stepData[state.stepData.length - 1].configItems === null
          ? state.stepData[state.stepData.length - 2].configItems
          : state.stepData[state.stepData.length - 1].configItems,
      text,
      value,
    } as CarInformation);
  }
  const index = newCarInformation.findIndex((x: CarInformation) => x.parameterName === parameter);
  return newCarInformation.slice(0, index + 1);
};

const GetConfigurationData = (updatedCarInformation: CarInformation[], state: FitAssistantState): [string, Step] => {
  const lastNoneNullConfigurationItem = [...updatedCarInformation]
    .reverse()
    .find((item) => item.configurationItem !== null);
  const configurationItem = lastNoneNullConfigurationItem?.configurationItem ?? null;
  const nextStep = state.initialData.Steps.find((x) => x.Id === configurationItem) ?? null;

  if (configurationItem === null || nextStep === null) {
    throw new Error('Missing configuration data: configurationItem and step must be defined');
  }

  return [configurationItem, nextStep];
};
